import { TimeSeriesConfig } from "@/components/graph/graph.types";
import { ParameterTimeSeriesConfiguration } from "@/lib/api";
import {
  coerceVisualizationType,
  GraphType,
  readVisualizationType,
  seriesParameterIds,
  visualizationTypeToGraphType,
} from "@/features/shared/widgets/model-map/model-map-visualization-types";
import type { TimeSeriesParameterOption } from "@/features/shared/widgets/map/time-series/time-series-parameter.types";

export { GraphType } from "@/features/shared/widgets/model-map/model-map-visualization-types";

type SeriesRow = ParameterTimeSeriesConfiguration["series"][number];

type MakeTimeSeriesConfigsOptions = {
  /** Maps parameterId → modelId for model-sourced parameters; observed params are absent. */
  parameterModelIndex?: Record<string, string>;
  /**
   * Fallback series type when `parameterModelIndex` is not provided.
   * Use `"model"` for map contexts where all parameters are model-sourced.
   * Defaults to `"observed"`.
   */
  defaultSeriesType?: "observed" | "model";
};

function mapSeriesForType(
  seriesRows: SeriesRow[],
  type: GraphType,
  options: MakeTimeSeriesConfigsOptions = {},
): TimeSeriesConfig["series"] {
  const { parameterModelIndex, defaultSeriesType = "observed" } = options;

  return seriesRows.map((s, i) => {
    const isModel = parameterModelIndex
      ? !!parameterModelIndex[s.parameter_id]
      : defaultSeriesType === "model";

    const color = isModel ? "green" : "blue";
    const line_style = isModel ? "dashed" : "solid";
    const line_width = isModel ? "regular" : "thick";
    const symbol = ["square", "triangle", "circle", "diamond", "triangle-down"][
      i % 5
    ] as string;

    if (type === GraphType.LineGraph) {
      return {
        parameter_id: s.parameter_id,
        label: s.label,
        color,
        line_width,
        line_style,
        symbol,
      };
    }
    if (type === GraphType.Pasquill) {
      return { parameter_id: s.parameter_id, label: s.label };
    }
    if (type === GraphType.Histogram || type === GraphType.AvaIndex) {
      return {
        parameter_id: s.parameter_id,
        label: s.label,
        color,
      };
    }
    return {
      parameter_id: s.parameter_id,
      label: s.label,
      color,
      symbol,
    };
  });
}

/**
 * Transforms admin-configured time series into viewer-ready configs.
 * Uses stored title, labels, unit, and `visualization_type`.
 */
export function makeTimeSeriesConfigs(
  configurations: ParameterTimeSeriesConfiguration[],
  parameters: TimeSeriesParameterOption[],
  options: MakeTimeSeriesConfigsOptions = {},
): TimeSeriesConfig[] {
  const paramMap = new Map(parameters.map((p) => [p.id, p]));

  return configurations
    .map((config) => {
      const resolvedSeries = config.series.filter((series) =>
        paramMap.has(series.parameter_id),
      );
      if (resolvedSeries.length === 0 || !config.unit?.code) return null;

      const viz = coerceVisualizationType(
        readVisualizationType(config),
        config.unit.code,
        seriesParameterIds(config),
      );
      const graphType = visualizationTypeToGraphType(viz);
      const series = mapSeriesForType(resolvedSeries, graphType, options);

      return {
        title: config.title,
        type: graphType,
        unit: config.unit,
        series,
        thresholds: config.thresholds,
      } as TimeSeriesConfig;
    })
    .filter((c): c is TimeSeriesConfig => c !== null);
}
